/**
 * Order Sheet / Basket Management System
 * Handles adding configurations to order sheet, managing items, and order processing
 */
class OrderSheetManager {
    constructor() {
        this.orderItems = [];
        this.uniqueIdCounter = 1;
        this.isSubmittingOrder = false;
        this.init();
    }

    init() {
        this.loadFromStorage();
        this.createOrderSheetElements();
        this.bindEvents();
        this.updateOrderSheetDisplay();
    }

    /**
     * Generate a unique ID for order items
     */
    generateUniqueId() {
        const timestamp = Date.now().toString(36);
        const random = Math.random().toString(36).substr(2, 5);
        return `ORD-${timestamp}-${random}`.toUpperCase();
    }

    /**
     * Create the order sheet HTML elements
     */
    createOrderSheetElements() {
        // Create order sheet tab
        const orderSheetTab = document.createElement('div');
        orderSheetTab.className = 'order-sheet-tab hidden';
        orderSheetTab.innerHTML = `
            <span>Order Sheet</span>
            <span class="order-sheet-count">0</span>
        `;
        orderSheetTab.id = 'order-sheet-tab';
        document.body.appendChild(orderSheetTab);

        // Create order sheet modal
        const orderSheetModal = document.createElement('div');
        orderSheetModal.className = 'order-sheet-modal';
        orderSheetModal.id = 'order-sheet-modal';
        orderSheetModal.innerHTML = `
            <div class="order-sheet-modal-content">
                <div class="order-sheet-header">
                    <h3 class="order-sheet-title">Order Sheet</h3>
                    <button class="order-sheet-close" id="order-sheet-close">&times;</button>
                </div>
                <div class="order-sheet-body" id="order-sheet-body">
                    <!-- Order items will be populated here -->
                </div>
                <div class="order-sheet-inline-notice" id="order-sheet-inline-notice" style="display: none;">
                    <h4 class="order-sheet-inline-notice__title" id="order-sheet-inline-notice-title"></h4>
                    <div class="order-sheet-inline-notice__content" id="order-sheet-inline-notice-content"></div>
                </div>
                <div class="order-sheet-footer" id="order-sheet-footer">
                    <!-- Footer content will be populated here -->
                </div>
            </div>
        `;
        document.body.appendChild(orderSheetModal);
    }

    /**
     * Bind event listeners
     */
    bindEvents() {
        // Order sheet tab click
        document.getElementById('order-sheet-tab').addEventListener('click', () => {
            this.showOrderSheet();
        });

        // Order sheet close button
        document.getElementById('order-sheet-close').addEventListener('click', () => {
            this.hideOrderSheet();
        });

        // Close modal when clicking outside
        document.getElementById('order-sheet-modal').addEventListener('click', (e) => {
            if (e.target.id === 'order-sheet-modal') {
                this.hideOrderSheet();
            }
        });

        // Escape key to close modal
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && document.getElementById('order-sheet-modal').classList.contains('active')) {
                this.hideOrderSheet();
            }
        });
    }

    /**
     * Add current configuration to order sheet
     */
    addCurrentConfiguration() {
        if (!window.ConfiguratorController) {
            console.error('ConfiguratorController not found');
            this.showNotification('Configurator not available. Please refresh the page and try again.', 'error');
            return false;
        }

        const configurator = window.ConfiguratorController;
        
        // Check if we have valid BOM data
        if (!configurator.materialBill || !configurator.canOrder) {
            this.showNotification('Unable to add configuration to order sheet. Please ensure all components are properly configured and pricing is available.', 'error');
            return false;
        }

        // Allow multiple items of the same type - no duplicate checking needed

        // Generate preview image
        let previewImage = null;
        try {
            if (configurator.StairController && configurator.StairController.ExportAsImage) {
                previewImage = configurator.StairController.ExportAsImage();
            }
        } catch (error) {
            console.warn('Failed to generate preview image:', error);
        }

        // Get configuration parameters for loading later
        let configurationParams = null;
        let dimensions = {};
        
        try {
            if (configurator.StairController && configurator.StairController.businessLogic) {
                const inputs = configurator.StairController.businessLogic.stairCaseParameters?.inputs || {};
                
                // Capture all user input dimensions based on configurator type
                if (configurator.type === 'STAIRCASE') {
                    dimensions = {
                        floor_height: inputs.floor_height || null,
                        angle: inputs.angle || null,
                        width: inputs.width || null,
                        riser_count: inputs.riser_count || null,
                        tread_count: inputs.tread_count || null
                    };
                    console.log('Captured staircase dimensions:', dimensions);
                    console.log('Captured staircase turns:', configurator.turns || []);
                } else if (configurator.type === 'BALUSTRADE') {
                    dimensions = {
                        width: inputs.width || null,
                        height: inputs.height || null,
                        angle: inputs.angle || null,
                        aperture_width: inputs.aperture_width || null,
                        aperture_height: inputs.aperture_height || null,
                        aperture_angle: inputs.aperture_angle || null,
                        glass_gap: inputs.glass_gap || null,
                        post_height: inputs.post_height || null
                    };
                    console.log('Captured balustrade dimensions:', dimensions);
                    console.log('Captured balustrade turns:', configurator.turns || []);
                }
                
                configurationParams = {
                    inputs: inputs,
                    options: configurator.options || {},
                    turns: configurator.turns || [],
                    dimensions: dimensions
                };
            }
        } catch (error) {
            console.warn('Failed to capture configuration parameters:', error);
        }

        // Generate order item data
        const orderItem = {
            id: this.generateUniqueId(),
            type: configurator.type || 'UNKNOWN',
            timestamp: new Date().toISOString(),
            previewImage: previewImage,
            dimensions: dimensions, // Add dimensions at top level for easy access
            turns: configurator.turns || [], // Add turns at top level for easy access
            configuration: {
                materialBill: JSON.parse(JSON.stringify(configurator.materialBill)), // Deep copy
                totalCost: configurator.totalBomCost,
                totalComponents: this.calculateTotalComponents(configurator.materialBill),
                dimensions: dimensions, // Also include in configuration for completeness
                turns: configurator.turns || [] // Also include turns in configuration for completeness
            },
            metadata: {
                userAgent: navigator.userAgent,
                url: window.location.href,
                configuratorVersion: configurator.version || 'unknown',
                configuration: configurationParams
            }
        };

        // Add to order items
        this.orderItems.push(orderItem);
        this.saveToStorage();
        this.updateOrderSheetDisplay();
        
        this.showNotification(`${orderItem.type} configuration added to order sheet (${this.orderItems.length} total items)`, 'success');
        
        // Scroll to order sheet tab if it's not visible
        setTimeout(() => {
            const tab = document.getElementById('order-sheet-tab');
            if (tab && !tab.classList.contains('hidden')) {
                tab.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
        }, 500);
        
        return true;
    }

    /**
     * Calculate total components from BOM
     */
    calculateTotalComponents(materialBill) {
        return Object.values(materialBill).reduce((total, item) => total + item.quantity, 0);
    }

    /**
     * Remove item from order sheet
     */
    removeOrderItem(itemId) {
        const index = this.orderItems.findIndex(item => item.id === itemId);
        if (index > -1) {
            this.orderItems.splice(index, 1);
            this.saveToStorage();
            this.updateOrderSheetDisplay();
            this.showNotification('Item removed from order sheet', 'info');
        }
    }

    /**
     * Show order sheet modal
     */
    showOrderSheet() {
        const modal = document.getElementById('order-sheet-modal');
        if (modal) {
            modal.classList.add('active');
            document.body.style.overflow = 'hidden';
        }
    }

    /**
     * Hide order sheet modal
     */
    hideOrderSheet() {
        const modal = document.getElementById('order-sheet-modal');
        if (modal) {
            modal.classList.remove('active');
            document.body.style.overflow = '';
        }
    }

    /**
     * Update order sheet display
     */
    updateOrderSheetDisplay() {
        this.updateOrderSheetTab();
        this.updateOrderSheetBody();
        this.updateOrderSheetNotice();
        this.updateOrderSheetFooter();
    }

    /**
     * Read order sheet notice config passed from Liquid.
     */
    getOrderSheetNoticeConfig() {
        const defaults = {
            title: '',
            content: '',
            backgroundColor: '#e8f4fd',
            textColor: '#1f3b57'
        };

        if (!window.PD_SC_ORDER_SHEET_NOTICE || typeof window.PD_SC_ORDER_SHEET_NOTICE !== 'object') {
            return defaults;
        }

        return {
            title: window.PD_SC_ORDER_SHEET_NOTICE.title || '',
            content: window.PD_SC_ORDER_SHEET_NOTICE.content || '',
            backgroundColor: window.PD_SC_ORDER_SHEET_NOTICE.backgroundColor || defaults.backgroundColor,
            textColor: window.PD_SC_ORDER_SHEET_NOTICE.textColor || defaults.textColor
        };
    }

    /**
     * Update order sheet tab
     */
    updateOrderSheetTab() {
        const tab = document.getElementById('order-sheet-tab');
        const count = document.querySelector('.order-sheet-count');
        
        if (this.orderItems.length > 0) {
            tab.classList.remove('hidden');
            count.textContent = this.orderItems.length;
        } else {
            tab.classList.add('hidden');
        }
    }

    /**
     * Update order sheet body content
     */
    updateOrderSheetBody() {
        const body = document.getElementById('order-sheet-body');
        
        if (this.orderItems.length === 0) {
            body.innerHTML = `
                <div class="order-sheet-empty">
                    <div class="order-sheet-empty-text">Your order sheet is empty</div>
                    <div class="order-sheet-empty-subtext">Add configurations from the bill of materials to get started</div>
                </div>
            `;
            return;
        }

        let html = '';
        this.orderItems.forEach(item => {
            const typeClass = item.type.toLowerCase();
            const totalCost = item.configuration.totalCost;
            const totalComponents = item.configuration.totalComponents;
            
            // Generate dimensions display based on type
            let dimensionsHtml = '';
            if (item.dimensions) {
                if (item.type === 'STAIRCASE') {
                    const dims = item.dimensions;
                    dimensionsHtml = `
                        <div class="order-item-dimensions">
                            <div class="order-item-dimension">
                                <span class="dimension-label">Floor Height:</span>
                                <span class="dimension-value">${dims.floor_height ? dims.floor_height + 'mm' : 'N/A'}</span>
                            </div>
                            <div class="order-item-dimension">
                                <span class="dimension-label">Width:</span>
                                <span class="dimension-value">${dims.width ? dims.width + 'mm' : 'N/A'}</span>
                            </div>
                            <div class="order-item-dimension">
                                <span class="dimension-label">Angle:</span>
                                <span class="dimension-value">${dims.angle ? dims.angle + '°' : 'N/A'}</span>
                            </div>
                            <div class="order-item-dimension">
                                <span class="dimension-label">Treads:</span>
                                <span class="dimension-value">${dims.riser_count || dims.tread_count || 'N/A'}</span>
                            </div>
                        </div>
                    `;
                } else if (item.type === 'BALUSTRADE') {
                    const dims = item.dimensions;
                    dimensionsHtml = `
                        <div class="order-item-dimensions">
                            <div class="order-item-dimension">
                                <span class="dimension-label">Width:</span>
                                <span class="dimension-value">${dims.width || dims.aperture_width ? (dims.width || dims.aperture_width) + 'mm' : 'N/A'}</span>
                            </div>
                            <div class="order-item-dimension">
                                <span class="dimension-label">Height:</span>
                                <span class="dimension-value">${dims.height || dims.aperture_height ? (dims.height || dims.aperture_height) + 'mm' : 'N/A'}</span>
                            </div>
                            <div class="order-item-dimension">
                                <span class="dimension-label">Angle:</span>
                                <span class="dimension-value">${dims.angle || dims.aperture_angle ? (dims.angle || dims.aperture_angle) + '°' : 'N/A'}</span>
                            </div>
                            ${dims.glass_gap ? `
                            <div class="order-item-dimension">
                                <span class="dimension-label">Glass Gap:</span>
                                <span class="dimension-value">${dims.glass_gap}mm</span>
                            </div>
                            ` : ''}
                        </div>
                    `;
                }
            }
            
            // Generate turns display if present
            let turnsHtml = '';
            if (item.turns && item.turns.length > 0) {
                const turnsList = item.turns.map((turn, index) => `
                    <div class="order-item-turn">
                        <span class="turn-label">Turn ${index + 1}:</span>
                        <span class="turn-value">${turn.turn_type} (${turn.turn_direction}) - ${turn.treads_before_turn} treads before, ${turn.width}mm width</span>
                    </div>
                `).join('');
                
                turnsHtml = `
                    <div class="order-item-turns">
                        <div class="order-item-turns-header">
                            <span class="turns-label">Turns (${item.turns.length}):</span>
                        </div>
                        <div class="order-item-turns-list">
                            ${turnsList}
                        </div>
                    </div>
                `;
            }
            
            // Generate preview HTML
            let previewHtml = '';
            if (item.previewImage) {
                previewHtml = `
                    <div class="order-item-preview">
                        <img src="${item.previewImage}" alt="${item.type} preview" class="order-item-preview-img" />
                    </div>
                `;
            } else {
                previewHtml = `
                    <div class="order-item-preview order-item-preview--placeholder">
                        <div class="order-item-preview-placeholder">
                            <div class="order-item-preview-icon">${item.type === 'STAIRCASE' ? '🏗️' : '🛡️'}</div>
                            <div class="order-item-preview-text">No Preview</div>
                        </div>
                    </div>
                `;
            }
            
            html += `
                <div class="order-item" data-item-id="${item.id}">
                    <div class="order-item-content">
                        ${previewHtml}
                        <div class="order-item-details">
                            <div class="order-item-header">
                                <div class="order-item-info">
                                    <div class="order-item-type ${typeClass}">${item.type}</div>
                                    <div class="order-item-id">${item.id}</div>
                                </div>
                                <button class="order-item-remove" onclick="event.stopPropagation(); window.OrderSheetManager.removeOrderItem('${item.id}')" title="Remove item">
                                    ×
                                </button>
                            </div>
                            ${dimensionsHtml}
                            ${turnsHtml}
                            <div class="order-item-stats">
                                <div class="order-item-stat">
                                    <div class="order-item-stat-label">Components</div>
                                    <div class="order-item-stat-value">${totalComponents}</div>
                                </div>
                                <div class="order-item-stat">
                                    <div class="order-item-stat-label">Total Cost</div>
                                    <div class="order-item-stat-value">£${totalCost.toFixed(2)}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            `;
        });

        body.innerHTML = html;
    }

    /**
     * Update order sheet inline notice shown between items and actions.
     */
    updateOrderSheetNotice() {
        const notice = document.getElementById('order-sheet-inline-notice');
        const noticeTitle = document.getElementById('order-sheet-inline-notice-title');
        const noticeContent = document.getElementById('order-sheet-inline-notice-content');

        if (!notice || !noticeTitle || !noticeContent) {
            return;
        }

        if (this.orderItems.length === 0) {
            notice.style.display = 'none';
            noticeTitle.textContent = '';
            noticeContent.innerHTML = '';
            return;
        }

        const noticeConfig = this.getOrderSheetNoticeConfig();
        const hasTitle = typeof noticeConfig.title === 'string' && noticeConfig.title.trim() !== '';
        const hasContent = typeof noticeConfig.content === 'string' && noticeConfig.content.trim() !== '';

        if (!hasTitle && !hasContent) {
            notice.style.display = 'none';
            noticeTitle.textContent = '';
            noticeContent.innerHTML = '';
            return;
        }

        notice.style.display = 'block';
        notice.style.backgroundColor = noticeConfig.backgroundColor;
        notice.style.color = noticeConfig.textColor;
        noticeTitle.style.display = hasTitle ? 'block' : 'none';
        noticeTitle.textContent = noticeConfig.title;
        noticeContent.innerHTML = noticeConfig.content;
    }

    /**
     * Update order sheet footer
     */
    updateOrderSheetFooter() {
        const footer = document.getElementById('order-sheet-footer');
        
        if (this.orderItems.length === 0) {
            footer.innerHTML = '';
            return;
        }

        const totalItems = this.orderItems.length;
        const totalCost = this.orderItems.reduce((sum, item) => sum + item.configuration.totalCost, 0);
        const totalComponents = this.orderItems.reduce((sum, item) => sum + item.configuration.totalComponents, 0);

        footer.innerHTML = `
            <div class="order-sheet-summary">
                <div class="order-sheet-summary-item">
                    <div class="order-sheet-summary-label">Items</div>
                    <div class="order-sheet-summary-value">${totalItems}</div>
                </div>
                <div class="order-sheet-summary-item">
                    <div class="order-sheet-summary-label">Components</div>
                    <div class="order-sheet-summary-value">${totalComponents}</div>
                </div>
                <div class="order-sheet-summary-item">
                    <div class="order-sheet-summary-label">Total Cost</div>
                    <div class="order-sheet-summary-value">£${totalCost.toFixed(2)}</div>
                </div>
            </div>
            <div class="order-sheet-actions">
                <button class="order-sheet-btn order-sheet-btn--secondary" onclick="window.OrderSheetManager.clearOrderSheet()">
                    Clear All
                </button>
                <button class="order-sheet-btn order-sheet-btn--primary" onclick="window.OrderSheetManager.processOrder()">
                    Order Now
                </button>
            </div>
        `;
    }

    /**
     * Clear all items from order sheet
     */
    clearOrderSheet() {
        if (this.orderItems.length === 0) return;
        
        this.showClearConfirmationModal();
    }

    /**
     * Show clear confirmation modal
     */
    showClearConfirmationModal() {
        // Create modal if it doesn't exist
        let modal = document.getElementById('order-sheet-clear-modal');
        if (!modal) {
            modal = document.createElement('div');
            modal.id = 'order-sheet-clear-modal';
            modal.className = 'order-sheet-modal';
            modal.innerHTML = `
                <div class="order-sheet-modal-content">
                    <div class="order-sheet-header">
                        <h3 class="order-sheet-title">Clear Order Sheet</h3>
                        <button class="order-sheet-close" id="order-sheet-clear-close">&times;</button>
                    </div>
                    <div class="order-sheet-body">
                        <div class="order-sheet-clear-content">
                            <div class="order-sheet-clear-title">Are you sure?</div>
                            <div class="order-sheet-clear-message">
                                This will remove all ${this.orderItems.length} item(s) from your order sheet. This action cannot be undone.
                            </div>
                        </div>
                    </div>
                    <div class="order-sheet-footer">
                        <div class="order-sheet-actions">
                            <button class="order-sheet-btn order-sheet-btn--secondary" id="order-sheet-clear-cancel">
                                Cancel
                            </button>
                            <button class="order-sheet-btn order-sheet-btn--primary" id="order-sheet-clear-confirm">
                                Clear All Items
                            </button>
                        </div>
                    </div>
                </div>
            `;
            document.body.appendChild(modal);
            
            // Bind events
            document.getElementById('order-sheet-clear-close').addEventListener('click', () => {
                this.hideClearConfirmationModal();
            });
            
            document.getElementById('order-sheet-clear-cancel').addEventListener('click', () => {
                this.hideClearConfirmationModal();
            });
            
            document.getElementById('order-sheet-clear-confirm').addEventListener('click', () => {
                this.confirmClearOrderSheet();
            });
            
            // Close on backdrop click
            modal.addEventListener('click', (e) => {
                if (e.target.id === 'order-sheet-clear-modal') {
                    this.hideClearConfirmationModal();
                }
            });
        }
        
        // Update message with current item count
        const messageElement = modal.querySelector('.order-sheet-clear-message');
        if (messageElement) {
            messageElement.textContent = `This will remove all ${this.orderItems.length} item(s) from your order sheet. This action cannot be undone.`;
        }
        
        modal.classList.add('active');
        document.body.style.overflow = 'hidden';
    }

    /**
     * Hide clear confirmation modal
     */
    hideClearConfirmationModal() {
        const modal = document.getElementById('order-sheet-clear-modal');
        if (modal) {
            modal.classList.remove('active');
            document.body.style.overflow = '';
        }
    }

    /**
     * Confirm clear order sheet
     */
    confirmClearOrderSheet() {
        this.orderItems = [];
        this.saveToStorage();
        this.updateOrderSheetDisplay();
        this.hideClearConfirmationModal();
        this.showNotification('Order sheet cleared', 'info');
    }

    /**
     * Process the order
     */
    processOrder() {
        if (this.orderItems.length === 0) {
            this.showNotification('No items in order sheet', 'error');
            return;
        }

        this.showOrderConfirmationModal();
    }

    /**
     * Show order confirmation modal
     */
    showOrderConfirmationModal() {
        const totalCost = this.orderItems.reduce((sum, item) => sum + item.configuration.totalCost, 0);
        const totalComponents = this.orderItems.reduce((sum, item) => sum + item.configuration.totalComponents, 0);
        
        // Create modal if it doesn't exist
        let modal = document.getElementById('order-sheet-confirm-modal');
        if (!modal) {
            modal = document.createElement('div');
            modal.id = 'order-sheet-confirm-modal';
            modal.className = 'order-sheet-modal';
            modal.innerHTML = `
                <div class="order-sheet-modal-content">
                    <div class="order-sheet-header">
                        <h3 class="order-sheet-title">Confirm Order</h3>
                        <button class="order-sheet-close" id="order-sheet-confirm-close">&times;</button>
                    </div>
                    <div class="order-sheet-body">
                        <div class="order-sheet-confirm-content">
                            <div class="order-sheet-confirm-title">Ready to Order?</div>
                            <div class="order-sheet-confirm-summary">
                                <div class="order-sheet-confirm-item">
                                    <span class="order-sheet-confirm-label">Items:</span>
                                    <span class="order-sheet-confirm-value" id="order-confirm-count">${this.orderItems.length}</span>
                                </div>
                                <div class="order-sheet-confirm-item">
                                    <span class="order-sheet-confirm-label">Components:</span>
                                    <span class="order-sheet-confirm-value" id="order-confirm-components">${totalComponents}</span>
                                </div>
                                <div class="order-sheet-confirm-item order-sheet-confirm-total">
                                    <span class="order-sheet-confirm-label">Total Cost:</span>
                                    <span class="order-sheet-confirm-value" id="order-confirm-cost">£${totalCost.toFixed(2)}</span>
                                </div>
                            </div>
                            <div class="order-sheet-confirm-message">
                                This will process your order into a unique checkout link and clear the order sheet. This action cannot be undone.
                            </div>
                        </div>
                    </div>
                    <div class="order-sheet-footer">
                        <div class="order-sheet-actions">
                            <button class="order-sheet-btn order-sheet-btn--secondary" id="order-sheet-confirm-cancel">
                                Cancel
                            </button>
                            <button class="order-sheet-btn order-sheet-btn--primary" id="order-sheet-confirm-process">
                                Process Order
                            </button>
                        </div>
                    </div>
                </div>
            `;
            document.body.appendChild(modal);
            
            // Bind events
            document.getElementById('order-sheet-confirm-close').addEventListener('click', () => {
                this.hideOrderConfirmationModal();
            });
            
            document.getElementById('order-sheet-confirm-cancel').addEventListener('click', () => {
                this.hideOrderConfirmationModal();
            });
            
            document.getElementById('order-sheet-confirm-process').addEventListener('click', () => {
                this.confirmProcessOrder();
            });
            
            // Close on backdrop click
            modal.addEventListener('click', (e) => {
                if (e.target.id === 'order-sheet-confirm-modal') {
                    this.hideOrderConfirmationModal();
                }
            });
        }
        
        // Update values
        modal.querySelector('#order-confirm-count').textContent = this.orderItems.length;
        modal.querySelector('#order-confirm-components').textContent = totalComponents;
        modal.querySelector('#order-confirm-cost').textContent = `£${totalCost.toFixed(2)}`;
        this.setOrderProcessingButtonState(this.isSubmittingOrder);
        
        modal.classList.add('active');
        document.body.style.overflow = 'hidden';
    }

    /**
     * Hide order confirmation modal
     */
    hideOrderConfirmationModal() {
        const modal = document.getElementById('order-sheet-confirm-modal');
        if (modal) {
            modal.classList.remove('active');
            document.body.style.overflow = '';
        }
    }

    /**
     * Toggle process-order button loading state
     */
    setOrderProcessingButtonState(isProcessing) {
        const processButton = document.getElementById('order-sheet-confirm-process');
        if (!processButton) return;
        processButton.disabled = isProcessing;
        processButton.setAttribute('aria-disabled', isProcessing ? 'true' : 'false');
        processButton.textContent = isProcessing ? 'Processing...' : 'Process Order';
    }

    /**
     * Show the global configurator loading cover if available
     */
    showLoadingCover() {
        if (typeof window.openLoader === 'function') {
            window.openLoader();
            return;
        }
        const loader = document.getElementById('sc-primary-loader');
        if (loader) {
            loader.style.display = 'block';
            loader.style.opacity = 1;
            document.body.style.overflow = 'hidden';
        }
    }

    /**
     * Hide the global configurator loading cover if available
     */
    hideLoadingCover() {
        if (typeof window.closeLoader === 'function') {
            window.closeLoader();
            return;
        }
        const loader = document.getElementById('sc-primary-loader');
        if (loader) {
            loader.style.display = 'none';
            loader.style.opacity = 0;
            document.body.style.overflow = '';
        }
    }

    /**
     * Resolve the draft-order endpoint from configurator settings
     */
    getDraftOrderEndpoint() {
        const configuredBaseUrl = (window.PD_SC_ORDER_URL || '').toString().trim();
        if (!configuredBaseUrl) {
            return null;
        }

        const normalizedBaseUrl = configuredBaseUrl.replace(/\/+$/, '');
        const endpoint = normalizedBaseUrl.toLowerCase().endsWith('/draft-order')
            ? normalizedBaseUrl
            : `${normalizedBaseUrl}/draft-order`;

        try {
            // Validate URL format early so we can show a user-friendly message.
            new URL(endpoint);
            return endpoint;
        } catch (error) {
            return null;
        }
    }

    /**
     * Confirm process order
     */
    async confirmProcessOrder() {
        if (this.isSubmittingOrder) {
            this.showNotification('Order submission is already in progress. Please wait.', 'warning');
            return;
        }

        const endpoint = this.getDraftOrderEndpoint();
        if (!endpoint) {
            this.showNotification('Order endpoint is not configured. Please set the API URL in the configurator settings.', 'error');
            return;
        }

        const totalCost = this.orderItems.reduce((sum, item) => sum + item.configuration.totalCost, 0);
        const totalComponents = this.orderItems.reduce((sum, item) => sum + item.configuration.totalComponents, 0);
        
        // Prepare order data
        const orderData = {
            items: this.orderItems,
            totalCost: totalCost,
            totalComponents: totalComponents,
            timestamp: new Date().toISOString(),
            orderId: this.generateUniqueId(),
            // Include comprehensive dimension and turns data for order processing
            dimensionsData: this.orderItems.map(item => ({
                id: item.id,
                type: item.type,
                dimensions: item.dimensions || {},
                turns: item.turns || [],
                configuration: {
                    inputs: item.metadata?.configuration?.inputs || {},
                    options: item.metadata?.configuration?.options || {}
                }
            }))
        };

        console.log('Order Data:', orderData);
        console.log('Dimensions Data for Order Processing:', orderData.dimensionsData);
        console.log('Turns Data for Order Processing:', orderData.dimensionsData.map(item => ({ id: item.id, type: item.type, turns: item.turns })));

        this.isSubmittingOrder = true;
        this.setOrderProcessingButtonState(true);
        this.hideOrderConfirmationModal();
        this.showLoadingCover();
        this.showNotification('Processing order...', 'info');

        let redirectStarted = false;
        try {
            console.info('Submitting configurator draft order', {
                orderId: orderData.orderId,
                endpoint
            });

            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(orderData)
            });

            let responsePayload = {};
            try {
                responsePayload = await response.json();
            } catch (parseError) {
                responsePayload = {};
            }

            if (!response.ok) {
                const errorMessage = responsePayload.message || responsePayload.error || 'Unable to process your order right now. Please try again.';
                throw new Error(errorMessage);
            }

            const invoiceUrl = responsePayload.invoiceUrl;
            if (!invoiceUrl || typeof invoiceUrl !== 'string') {
                throw new Error('Order was created but no checkout URL was returned.');
            }

            console.info('Draft order created successfully', {
                orderId: orderData.orderId,
                draftOrderId: responsePayload.draftOrderId || null,
                status: response.status
            });

            // Clear the order sheet once a valid checkout URL has been returned.
            this.orderItems = [];
            this.saveToStorage();
            this.updateOrderSheetDisplay();

            // Allow backend time to fully finalise the draft before navigating.
            await new Promise((resolve) => setTimeout(resolve, 3000));
            redirectStarted = true;
            window.location.href = invoiceUrl;
        } catch (error) {
            console.error('Draft-order submission failed', {
                orderId: orderData.orderId,
                endpoint,
                error: error?.message || error
            });
            this.showNotification(error?.message || 'Unable to process your order right now. Please try again.', 'error');
        } finally {
            this.isSubmittingOrder = false;
            this.setOrderProcessingButtonState(false);
            if (!redirectStarted) {
                this.hideLoadingCover();
            }
        }
    }

    /**
     * Show notification
     */
    showNotification(message, type = 'info') {
        // Create our own notification system to avoid conflicts
        this.createOrderSheetNotification(message, type);
    }

    /**
     * Create order sheet specific notification
     */
    createOrderSheetNotification(message, type = 'info') {
        // Use the main configurator notification system if available
        if (window.ConfiguratorController && typeof window.ConfiguratorController.showMainConfiguratorNotification === 'function') {
            window.ConfiguratorController.showMainConfiguratorNotification(message, type);
            return;
        }

        // Fallback to standalone notification system
        // Remove any existing order sheet notifications
        const existingNotifications = document.querySelectorAll('.order-sheet-notification');
        existingNotifications.forEach(notification => notification.remove());

        const notification = document.createElement('div');
        notification.className = `order-sheet-notification order-sheet-notification--${type}`;
        
        // Set styles directly to avoid conflicts
        const colors = {
            success: { bg: '#d4edda', border: '#c3e6cb', text: '#155724' },
            error: { bg: '#f8d7da', border: '#f5c6cb', text: '#721c24' },
            warning: { bg: '#fff3cd', border: '#ffeaa7', text: '#856404' },
            info: { bg: '#d1ecf1', border: '#bee5eb', text: '#0c5460' }
        };
        
        const colorScheme = colors[type] || colors.info;
        
        notification.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            background: ${colorScheme.bg};
            border: 1px solid ${colorScheme.border};
            color: ${colorScheme.text};
            padding: 15px 20px;
            border-radius: 8px;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
            z-index: 10001;
            max-width: 400px;
            font-family: inherit;
            font-size: 14px;
            font-weight: 500;
            line-height: 1.4;
            animation: orderSheetSlideIn 0.3s ease-out;
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            gap: 12px;
        `;
        
        notification.innerHTML = `
            <div style="flex: 1;">${message}</div>
            <button onclick="this.parentElement.remove()" style="
                background: none;
                border: none;
                font-size: 18px;
                cursor: pointer;
                color: inherit;
                opacity: 0.7;
                transition: opacity 0.2s ease;
                padding: 0;
                line-height: 1;
            " onmouseover="this.style.opacity='1'" onmouseout="this.style.opacity='0.7'">×</button>
        `;
        
        // Add animation keyframes if not already added
        if (!document.getElementById('order-sheet-notification-styles')) {
            const style = document.createElement('style');
            style.id = 'order-sheet-notification-styles';
            style.textContent = `
                @keyframes orderSheetSlideIn {
                    from { 
                        transform: translateX(100%); 
                        opacity: 0; 
                    }
                    to { 
                        transform: translateX(0); 
                        opacity: 1; 
                    }
                }
            `;
            document.head.appendChild(style);
        }
        
        document.body.appendChild(notification);
        
        // Auto-remove after 5 seconds
        setTimeout(() => {
            if (notification.parentElement) {
                notification.style.animation = 'orderSheetSlideIn 0.3s ease-out reverse';
                setTimeout(() => notification.remove(), 300);
            }
        }, 5000);
    }

    /**
     * Save order items to localStorage
     */
    saveToStorage() {
        try {
            console.log(JSON.stringify(this.orderItems[0]))
            localStorage.setItem('staircrazy_order_sheet', JSON.stringify(this.orderItems));
        } catch (error) {
            console.error('Failed to save order sheet to storage:', error);
        }
    }

    /**
     * Load order items from localStorage
     */
    loadFromStorage() {
        try {
            const stored = localStorage.getItem('staircrazy_order_sheet');
            if (stored) {
                this.orderItems = JSON.parse(stored);
            }
        } catch (error) {
            console.error('Failed to load order sheet from storage:', error);
            this.orderItems = [];
        }
    }

    /**
     * Load configuration from order sheet into configurator
     */
    loadConfiguration(itemId) {
        const item = this.orderItems.find(item => item.id === itemId);
        if (!item) {
            this.showNotification('Configuration not found', 'error');
            return;
        }

        if (!window.ConfiguratorController) {
            this.showNotification('Configurator not available', 'error');
            return;
        }

        const configurator = window.ConfiguratorController;
        
        // Close the order sheet modal
        this.hideOrderSheet();
        
        // Show loading notification
        this.showNotification('Loading configuration...', 'info');
        
        try {
            // Switch configurator type if needed
            if (configurator.type !== item.type) {
                configurator.setConfiguratorType(item.type);
                configurator.StairController = new StairCaseController(
                    item.type,
                    null,
                    true
                );
                configurator.StairController.registerCallback('onCalculateBillOfMaterials', (data) => {
                    configurator.updateBillOfMaterials(data);
                });
                configurator.StairController.registerCallback('treadBoundsUpdated', (minTreads, maxTreads) => {
                    configurator.updateTreadCountValidation(minTreads, maxTreads);
                });
                configurator.ShowControls();
            }
            
            // Load the configuration parameters
            if (item.metadata && item.metadata.configuration) {
                const config = item.metadata.configuration;
                
                // Set inputs
                if (config.inputs) {
                    for (const [key, value] of Object.entries(config.inputs)) {
                        configurator.StairController.setInput(key, value, false);
                    }
                }
                
                // Set options
                if (config.options) {
                    configurator.StairController.setSelectedOptions(config.options);
                    configurator.options = {
                        ...config.options,
                        misc: config.options.misc ? [...config.options.misc] : []
                    };
                }
                
                // Load turns if available
                if (config.turns && Array.isArray(config.turns)) {
                    configurator.turns = [...config.turns];
                    configurator.StairController.setTurns(configurator.turns);
                }
                
                // Generate the configuration
                configurator.StairController.generate();
                
                this.showNotification(`${item.type} configuration loaded successfully`, 'success');
            } else {
                // Fallback: try to reconstruct from material bill
                this.showNotification('Configuration loaded (limited data available)', 'warning');
            }
            
        } catch (error) {
            console.error('Error loading configuration:', error);
            this.showNotification('Error loading configuration', 'error');
        }
    }

    /**
     * Get order sheet data (for external access)
     */
    getOrderData() {
        return {
            items: this.orderItems,
            totalCost: this.orderItems.reduce((sum, item) => sum + item.configuration.totalCost, 0),
            totalComponents: this.orderItems.reduce((sum, item) => sum + item.configuration.totalComponents, 0),
            itemCount: this.orderItems.length,
            // Include summary of all dimensions and turns for easy access
            dimensionsSummary: this.orderItems.map(item => ({
                id: item.id,
                type: item.type,
                dimensions: item.dimensions || {},
                turns: item.turns || []
            }))
        };
    }
}

// Initialize order sheet manager when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
    window.OrderSheetManager = new OrderSheetManager();
});

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
    module.exports = OrderSheetManager;
}
